import React from 'react';

import { CardTypeRow } from '../shared/CardTypeRow';

import type { TieredRates } from '../../../../../utils/rateExtractor';

interface CardSpecificSectionProps {
	// Separate objects for 3-tier and 6-tier
	threeTierRates: {
		visa_mastercard_discover_credit: TieredRates;
		american_express: TieredRates;
	};
	sixTierRates: {
		visa_mastercard_discover_credit: TieredRates; // with_debit rates
		visa_mastercard_discover_debit: TieredRates;
		pin_debit: TieredRates;
	};
	sameAsAbove: boolean;
	onThreeTierChange: (
		cardType: string,
		tier: keyof TieredRates,
		value: number,
	) => void;
	onSixTierChange: (
		cardType: string,
		tier: keyof TieredRates,
		value: number,
	) => void;
	onSameAsAboveChange: (checked: boolean) => void;
}

export const CardSpecificSection: React.FC<CardSpecificSectionProps> = ({
	threeTierRates,
	sixTierRates,
	sameAsAbove,
	onThreeTierChange,
	onSixTierChange,
	onSameAsAboveChange,
}) => {
	return (
		<div className="space-y-8">
			{/* 3-tier Pricing Model */}
			<div className="space-y-6">
				<div>
					<h4 className="text-lg font-semibold text-gray-900 mt-4">
						3-tier Pricing Model
					</h4>
					<h4 className="text-sm text-gray-600">
						Credit card network only (credit_only)
					</h4>
				</div>

				{/* Header Row */}
				<div className="grid grid-cols-7 gap-4 items-center">
					<div className="font-medium col-span-1 text-gray-900">Card Type</div>
					<div className="col-span-2">
						<span className="text-sm font-semibold text-gray-700">
							Qualified
						</span>
					</div>
					<div className="col-span-2">
						<span className="text-sm font-semibold text-gray-700">
							Mid-Qualified
						</span>
					</div>
					<div className="col-span-2">
						<span className="text-sm font-semibold text-gray-700">
							Non-Qualified
						</span>
					</div>
				</div>

				{/* Row 1: Visa / Mastercard / Discover Credit (3-tier) */}
				<CardTypeRow
					cardTypeName="Visa / Mastercard / Discover Credit"
					rates={threeTierRates.visa_mastercard_discover_credit}
					onRateChange={(tier, value) => {
						onThreeTierChange('visa_mastercard_discover_credit', tier, value);
					}}
				/>

				{/* Row 2: American Express (3-tier) */}
				<CardTypeRow
					cardTypeName="American Express"
					rates={threeTierRates.american_express}
					onRateChange={(tier, value) => {
						onThreeTierChange('american_express', tier, value);
					}}
				/>
			</div>

			{/* 6-tier Pricing Model */}
			<div className="space-y-6">
				<div className="flex items-center justify-between mb-4">
					<div>
						<h4 className="text-lg font-semibold text-gray-900 mb-1">
							6-tier Pricing Model
						</h4>
						<p className="text-sm text-gray-600">
							Credit + debit card networks (with_debit)
						</p>
					</div>
					<div className="flex items-center">
						<input
							type="checkbox"
							id="sameAsAbove"
							checked={sameAsAbove}
							onChange={(e) => onSameAsAboveChange(e.target.checked)}
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<label htmlFor="sameAsAbove" className="ml-2 text-sm text-gray-700">
							Same as above
						</label>
					</div>
				</div>

				{/* Header Row */}
				<div className="grid grid-cols-7 gap-6 items-center">
					<div className="font-medium text-gray-900">Card Type</div>
					<div className="col-span-2">
						<span className="text-sm font-semibold text-gray-700">
							Qualified
						</span>
					</div>
					<div className="col-span-2">
						<span className="text-sm font-semibold text-gray-700">
							Mid-Qualified
						</span>
					</div>
					<div className="col-span-2">
						<span className="text-sm font-semibold text-gray-700">
							Non-Qualified
						</span>
					</div>
				</div>

				{/* Row 1: Visa / Mastercard / Discover Credit (6-tier - with_debit) */}
				<CardTypeRow
					cardTypeName="Visa / Mastercard / Discover Credit"
					rates={
						sameAsAbove
							? threeTierRates.visa_mastercard_discover_credit
							: sixTierRates.visa_mastercard_discover_credit
					}
					onRateChange={(tier, value) => {
						if (sameAsAbove) {
							// When "Same as above" is checked, update both 3-tier and 6-tier
							onThreeTierChange('visa_mastercard_discover_credit', tier, value);
							onSixTierChange('visa_mastercard_discover_credit', tier, value);
						} else {
							// When "Same as above" is unchecked, only update 6-tier
							onSixTierChange('visa_mastercard_discover_credit', tier, value);
						}
					}}
				/>

				{/* Row 2: Visa / Mastercard / Discover Debit */}
				<CardTypeRow
					cardTypeName="Visa / Mastercard / Discover Debit"
					rates={sixTierRates.visa_mastercard_discover_debit}
					onRateChange={(tier, value) => {
						onSixTierChange('visa_mastercard_discover_debit', tier, value);
					}}
				/>

				{/* Row 3: Pin Debit */}
				<CardTypeRow
					cardTypeName="Pin Debit"
					rates={sixTierRates.pin_debit}
					onRateChange={(tier, value) => {
						onSixTierChange('pin_debit', tier, value);
					}}
				/>
			</div>
		</div>
	);
};
